import { callZohoAPI } from '../utils/api'
import { errorLogService } from './errorLog'

// Zoho Creator API paths
const CREATOR_BASE = 'creator/v2.1/data/52studios/campaigns'
const SIGNUP_FORM = `${CREATOR_BASE}/form/Sign_Up_Forms`
const SIGNUP_REPORT = `${CREATOR_BASE}/report/Sign_Up_Forms_Report`
const EMAIL_FORM = `${CREATOR_BASE}/form/Email_Templates`
const EMAIL_REPORT = `${CREATOR_BASE}/report/All_Email_Templates`
const FORM_CONFIG_FORM = `${CREATOR_BASE}/form/Form_Configurations`
const FORM_CONFIG_REPORT = `${CREATOR_BASE}/report/All_Form_Configurations`
const NEWSLETTER_FORM = `${CREATOR_BASE}/form/Newsletters`
const NEWSLETTER_REPORT = `${CREATOR_BASE}/report/All_Newsletters`
const SUBMISSION_FORM = `${CREATOR_BASE}/form/Submissions`
const SUBMISSION_REPORT = `${CREATOR_BASE}/report/All_Submissions`
const TEAM_FORM = `${CREATOR_BASE}/form/Team_Configurations`
const TEAM_REPORT = `${CREATOR_BASE}/report/All_Team_Configurations`
const CAMPAIGN_FORM = `${CREATOR_BASE}/form/Campaigns`
const CAMPAIGN_REPORT = `${CREATOR_BASE}/report/All_Campaigns`
const USER_FORM = `${CREATOR_BASE}/form/Users`
const USER_REPORT = `${CREATOR_BASE}/report/All_Users`
const ROLE_FORM = `${CREATOR_BASE}/form/Roles`
const ROLE_REPORT = `${CREATOR_BASE}/report/All_Roles`
const AUDIENCE_FORM = `${CREATOR_BASE}/form/Audiences`
const AUDIENCE_REPORT = `${CREATOR_BASE}/report/All_Audiences`
const FORM_TEMPLATE_FORM = `${CREATOR_BASE}/form/Form_Templates`
const FORM_TEMPLATE_REPORT = `${CREATOR_BASE}/report/All_Form_Templates`
const EMAIL_LIBRARY_FORM = `${CREATOR_BASE}/form/Email_Template_Libraries`
const EMAIL_LIBRARY_REPORT = `${CREATOR_BASE}/report/All_Email_Template_Libraries`
const LANDING_PAGE_FORM = `${CREATOR_BASE}/form/Landing_Pages`
const LANDING_PAGE_REPORT = `${CREATOR_BASE}/report/All_Landing_Pages`
const MEDIA_LIBRARY_FORM = `${CREATOR_BASE}/form/Media_Library`
const MEDIA_LIBRARY_REPORT = `${CREATOR_BASE}/report/Media_Library_Report`
const AUDIT_LOG_FORM = `${CREATOR_BASE}/form/Audit_Logs`
const AUDIT_LOG_REPORT = `${CREATOR_BASE}/report/All_Audit_Logs`
const FORM_VERSION_FORM = `${CREATOR_BASE}/form/Form_Version_Histories`
const FORM_VERSION_REPORT = `${CREATOR_BASE}/report/All_Form_Version_Histories`
const EMAIL_VERSION_FORM = `${CREATOR_BASE}/form/Email_Template_Version_Histories`
const EMAIL_VERSION_REPORT = `${CREATOR_BASE}/report/All_Email_Template_Version_Histories`

// ── Types ──

export interface SignupFormRecord {
  ID: string
  Form_Name: string
  Url_Slug: string
  Status: string
  HTML: string
  CSS: string
  JavaScript1: string
  JavaScript_Embed: string
  Iframe_Embed: string
  Team: string
  Region: string
  Builder_Schema?: string
  Builder_Mode?: string
}

export interface EmailTemplateRecord {
  ID: string
  Email_Subject: string
  Preheader_Text: string
  Email_HTML_Body: string
  Confirmation_Link: string
  Sign_Up_Form: string
  Team: string
  Region: string
  Builder_Schema?: string
  Builder_Mode?: string
}

export interface FormConfigRecord {
  ID: string
  Sign_Up_Form: string
  Email_Template: string
  Team: string
  Region: string
}

// Newsletter subform row types
export interface NewsletterTagRow {
  ID?: string // Present when read from API; include in updates to preserve row identity
  Tag_ID: string
  Tag_Name: string
}

export interface NewsletterCategoryRuleRow {
  ID?: string
  Form_Field: string
  Condition: string
  Value: string
  Category_Name: string
  Category_ID: string
}

export interface NewsletterMailingListRow {
  ID?: string
  Mailing_List_Name: string
  Mailing_List_ID: string
}

export interface NewsletterRecord {
  ID: string
  Newsletter: string
  Newsletter_ID: string
  Sign_Up_Form: string
  Team: string
  Region: string
  // Mailing_List_Name: string // REMOVED - replaced by subform
  // Mailing_List_ID: string   // REMOVED - replaced by subform
  Mailing_Lists?: NewsletterMailingListRow[] // New subform
  Newsletter_Tags?: NewsletterTagRow[]
  Newsletter_Category_Rules?: NewsletterCategoryRuleRow[]
}

// Submission types
export interface SubmissionRecord {
  ID: string
  Form_Payload: string
  Status: string
  Newsletter_ID: string
  Team: string
  Region: string
  Confirmation_Token?: string
  Confirmed_At?: string
  Confirmed_At1?: string // New field for Confirmed At
  Redirect_URL?: string
  Rendered_Email_HTML?: string
  Rendered_Email_Subject?: string
  Subscriber_Email?: string
  Added_Time?: string
}

// Team types
export interface TeamRegionRow {
  ID?: string
  Region_Name: string
}

export interface TeamRecord {
  ID: string
  Team_Name: string
  Team_ID: string
  Logo_Url?: string
  Primary_Color?: string
  Team_Regions?: TeamRegionRow[]
}

// Campaign types
export interface CampaignTargetRegionRow {
  ID?: string
  Region_Name: string
}

export interface CampaignTargetMailingListRow {
  ID?: string
  Mailing_List_ID: string
  Mailing_List_Name: string
}

export interface CampaignTargetTagRow {
  ID?: string
  Tag_ID: string
  Tag_Name: string
}

export interface CampaignRecord {
  ID: string
  Campaign_Name: string
  Campaign_ID: string
  Email_Subject: string
  Preheader_Text: string
  From_Name: string
  From_Email: string
  HTML_Content: string
  Text_Content: string
  Status: string // "Draft" | "Exported" | "Sent"
  Bucket_ID: string
  Bucket_Expires_At: string
  Recipient_Count: number
  Exported_At?: string // Exists in Creator form but not sent on create
  Sent_At?: string
  Team: string
  Region: string
  Campaign_Target_Regions?: CampaignTargetRegionRow[]
  Campaign_Target_Mailing_Lists?: CampaignTargetMailingListRow[]
  Campaign_Target_Tags?: CampaignTargetTagRow[]
}

// User types
export interface UserRegionRow {
  ID?: string
  Region_Name: string
}

export interface UserRecord {
  ID: string
  User_Name: string
  Email: string
  Role: 'Super Admin' | 'Team Manager' | 'Operator' | 'Viewer'
  Team: string
  Status: 'Active' | 'Suspended' | 'Invited'
  Invited_By: string
  User_Regions?: UserRegionRow[]

  // Auth Fields
  Password_Hash?: string
  Password_Salt?: string
  Quick_PIN_Hash?: string
  Quick_PIN_Salt?: string
  Password_Set?: boolean
  Reset_Token?: string
  Reset_Token_Expires?: string
  Session_Token?: string
  Last_Full_Login?: string
  Phone_Number?: string
}

// Role types
export interface RolePermissionRow {
  ID?: string
  Permission_Key: string
}

export interface RoleRecord {
  ID: string
  Role_Name: string
  Description: string
  Is_System: boolean
  Role_Permissions?: RolePermissionRow[]
}

// Audience types
export interface AudienceRecord {
  ID: string
  Audience_ID: string
  Audience_Name: string
  Description: string
  Team: string
  Region: string
  Rules_JSON: string
  Logic: string
  Last_Count: number
}

// Form Template Library types
export interface FormTemplateRecord {
  ID: string
  Template_Name: string
  Description: string
  HTML: string
  CSS: string
  JavaScript1: string
  Builder_Schema: string
  Builder_Mode: string
  Category: string
  Team: string
  Region: string
}

// Email Template Library types
export interface EmailLibraryRecord {
  ID: string
  Template_Name: string
  Description: string
  Email_HTML_Body: string
  Email_Subject: string
  Preheader_Text: string
  Builder_Schema: string
  Builder_Mode: string
  Category: string
  Team: string
  Region: string
}

// Landing Page types
export interface LandingPageRecord {
  ID: string
  Page_Name: string
  Url_Slug: string
  Status: string
  HTML: string
  CSS: string
  JavaScript1: string
  Builder_Schema?: string
  Builder_Mode?: string
  Team: string
  Region: string
}

// Media Library types
export interface MediaLibraryRecord {
  ID: string
  Image_Name: string
  Image_URL: string
  Description: string
  Category: string
  Team: string
  Region: string
}

// Audit Log types
export interface AuditLogRecord {
  ID: string
  Action_Type: string
  Entity_Type: string
  Entity_ID: string
  Entity_Name: string
  Summary?: string
  User_Name: string
  User_Email: string
  User_Role: string
  Team: string
  Region: string
  IP_Address?: string
  Changes_JSON?: string
  Metadata_JSON?: string
  created?: string // Unix timestamp (seconds)
}

// Version History types
export interface FormVersionRecord {
  ID: string
  Form_ID: string
  Form_Name: string
  Version_Number: number
  Version_Label?: string
  Builder_Schema: string
  HTML: string
  CSS: string
  JavaScript1: string
  Created_By_Name: string
  Created_By_Email: string
  Change_Summary?: string
  Team: string
  Region: string
  Added_Time?: string
  Is_Active?: boolean // Checkbox field — tracks which version is currently active
}

export interface EmailTemplateVersionRecord {
  ID: string
  Template_ID: string
  Template_Name: string
  Version_Number: number
  Version_Label?: string
  Builder_Schema: string
  Email_HTML_Body: string
  Email_Subject: string
  Preheader_Text: string
  Created_By_Name: string
  Created_By_Email: string
  Change_Summary?: string
  Team: string
  Region: string
  Added_Time?: string
  Is_Active?: boolean // Checkbox field — tracks which version is currently active
}

// ── Helpers ──

function isNoRecordsError(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return (
    msg.includes('3100') ||
    msg.includes('9220') ||
    msg.toLowerCase().includes('no records')
  )
}

export interface CreatorPaginatedResult<T> {
  data: T[]
  total_count: number
}

// Filter types for server-side filtering via callZohoAPI data.filters
export type CreatorFilter =
  | { field: string; value: string; op?: '=' | '!=' | '>' | '<' | 'like' }
  | { fields: string[]; value: string }

function handleListResponse<T>(response: any): CreatorPaginatedResult<T> {
  const empty: CreatorPaginatedResult<T> = { data: [], total_count: 0 }
  if (!response) return empty
  if (response.code === 3100 || response.code === 9220) return empty
  if (response.data && Array.isArray(response.data)) {
    return {
      data: response.data as T[],
      total_count: parseInt(response.total_count, 10) || response.data.length,
    }
  }
  // Handle nested proxy envelope: { result: { data: [...] } }
  if (response.result && typeof response.result === 'object') {
    const inner = response.result
    if (inner.code === 3100 || inner.code === 9220) return empty
    if (inner.data && Array.isArray(inner.data)) {
      return {
        data: inner.data as T[],
        total_count: parseInt(inner.total_count, 10) || inner.data.length,
      }
    }
  }
  // If response itself is an array (some proxy formats)
  if (Array.isArray(response)) {
    return { data: response as T[], total_count: response.length }
  }
  // Log unexpected shape for debugging
  if (response.code === 3000 || response.data !== undefined) {
    console.warn(
      '[handleListResponse] Unexpected response shape:',
      JSON.stringify(response).substring(0, 500),
    )
  }
  return empty
}

function logAndThrow(action: string, err: unknown, payload?: any): never {
  const message = err instanceof Error ? err.message : 'Unknown error'
  const debug = (err as any)?.debug
  errorLogService.log(action, message, {
    ...(payload ? { sentPayload: payload } : {}),
    ...(debug ? { debug } : {}),
  })
  throw err
}

// ── Sign Up Forms CRUD ──

export const signupFormsApi = {
  list: async (): Promise<SignupFormRecord[]> => {
    try {
      const response = await callZohoAPI(SIGNUP_REPORT)
      return handleListResponse<SignupFormRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Signup Forms', err)
    }
  },

  get: async (id: string): Promise<SignupFormRecord | null> => {
    try {
      const response = await callZohoAPI(`${SIGNUP_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Signup Form', err, { id })
    }
  },

  findBySlug: async (slug: string): Promise<SignupFormRecord | null> => {
    // Try filter search first (more efficient)
    try {
      const filters: CreatorFilter[] = [{ field: 'Url_Slug', value: slug }]
      const response = await callZohoAPI(SIGNUP_REPORT, 'GET', { filters })
      const { data: records } = handleListResponse<SignupFormRecord>(response)
      if (records.length > 0) return records[0]
    } catch (err) {
      // If filter search fails with a real API error, propagate it
      // so callers can distinguish "API down" from "form not found"
      if (!isNoRecordsError(err)) {
        console.warn('findBySlug filter search failed:', err)
        throw err
      }
    }

    // Fallback to list all if filter returned nothing or had a no-records error
    try {
      const all = await signupFormsApi.list()
      return all.find((f) => f.Url_Slug === slug) || null
    } catch (err) {
      if (isNoRecordsError(err)) return null
      // Propagate real API errors
      throw err
    }
  },

  create: async (data: Partial<Omit<SignupFormRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(SIGNUP_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Signup Form', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<SignupFormRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${SIGNUP_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Signup Form', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${SIGNUP_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Signup Form', err, { id })
    }
  },
}

// ── Email Templates CRUD ──

export const emailTemplatesApi = {
  list: async (): Promise<EmailTemplateRecord[]> => {
    try {
      const response = await callZohoAPI(EMAIL_REPORT)
      return handleListResponse<EmailTemplateRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Email Templates', err)
    }
  },

  get: async (id: string): Promise<EmailTemplateRecord | null> => {
    try {
      const response = await callZohoAPI(`${EMAIL_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Email Template', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<EmailTemplateRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(EMAIL_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Email Template', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<EmailTemplateRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${EMAIL_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Email Template', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${EMAIL_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Email Template', err, { id })
    }
  },
}

// ── Form Configurations CRUD ──

export const formConfigsApi = {
  list: async (): Promise<FormConfigRecord[]> => {
    try {
      // field_config=all ensures lookup fields return display_value + ID (not just ID)
      const response = await callZohoAPI(
        `${FORM_CONFIG_REPORT}?field_config=all`,
      )
      return handleListResponse<FormConfigRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Form Configurations', err)
    }
  },

  get: async (id: string): Promise<FormConfigRecord | null> => {
    try {
      // field_config=all ensures lookup fields return display_value + ID
      const response = await callZohoAPI(
        `${FORM_CONFIG_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Form Configuration', err, { id })
    }
  },

  findByTemplateId: async (
    templateId: string,
  ): Promise<FormConfigRecord | null> => {
    try {
      const all = await formConfigsApi.list()
      return (
        all.find((c) => {
          const id = extractLookupId(c.Email_Template)
          return id === templateId
        }) || null
      )
    } catch {
      return null
    }
  },

  findByFormId: async (formId: string): Promise<FormConfigRecord[]> => {
    try {
      const all = await formConfigsApi.list()
      const matches = all.filter((c) => {
        const id = extractLookupId(c.Sign_Up_Form)
        return id === formId
      })
      console.log('[formConfigsApi.findByFormId]', {
        formId,
        totalConfigs: all.length,
        matchesFound: matches.length,
        allFormIds: all.map((c) => ({
          configId: c.ID,
          signUpForm: c.Sign_Up_Form,
          extractedId: extractLookupId(c.Sign_Up_Form),
        })),
      })
      return matches
    } catch (err) {
      console.error('[formConfigsApi.findByFormId] ❌ API call failed:', err)
      return []
    }
  },

  create: async (data: Partial<Omit<FormConfigRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(FORM_CONFIG_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Form Configuration', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<FormConfigRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_CONFIG_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Form Configuration', err, { id, ...data })
    }
  },

  upsertByTemplate: async (
    templateId: string,
    formId: string,
    team: string,
  ): Promise<{ action: 'created' | 'updated' }> => {
    const existing = await formConfigsApi.findByTemplateId(templateId)
    const payload = {
      Sign_Up_Form: formId,
      Email_Template: templateId,
      Team: team,
    }
    if (existing) {
      await formConfigsApi.update(existing.ID, payload)
      return { action: 'updated' }
    } else {
      await formConfigsApi.create(payload)
      return { action: 'created' }
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_CONFIG_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Form Configuration', err, { id })
    }
  },
}

// ── Newsletters CRUD ──

export const newslettersApi = {
  list: async (): Promise<NewsletterRecord[]> => {
    try {
      const response = await callZohoAPI(NEWSLETTER_REPORT)
      return handleListResponse<NewsletterRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Newsletters', err)
    }
  },

  get: async (id: string): Promise<NewsletterRecord | null> => {
    try {
      const response = await callZohoAPI(`${NEWSLETTER_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Newsletter', err, { id })
    }
  },

  create: async (data: Partial<Omit<NewsletterRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(NEWSLETTER_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Newsletter', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<NewsletterRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${NEWSLETTER_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Newsletter', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${NEWSLETTER_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Newsletter', err, { id })
    }
  },

  generateId: (): string => {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2, 8)
    return `nl_${timestamp}_${random}`
  },
}

// ── Submissions CRUD ──

export const submissionsApi = {
  list: async (options?: {
    maxRecords?: number
    offset?: number
    filters?: CreatorFilter[]
    only_count?: boolean
  }): Promise<any> => {
    try {
      const url = options?.maxRecords
        ? `${SUBMISSION_REPORT}?max_records=${options.maxRecords}`
        : SUBMISSION_REPORT
      const params: Record<string, any> = {}
      if (options?.offset) params.offset = options.offset
      if (options?.filters && options.filters.length > 0)
        params.filters = options.filters
      if (options?.only_count) params.only_count = true
      const response = await callZohoAPI(
        url,
        'GET',
        Object.keys(params).length > 0 ? params : undefined,
      )
      if (options?.only_count) {
        return {
          data: [],
          total_count: parseInt(response?.total_count, 10) || 0,
        }
      }
      return handleListResponse<SubmissionRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return { data: [], total_count: 0 }
      logAndThrow('List Submissions', err)
    }
  },

  get: async (id: string): Promise<SubmissionRecord | null> => {
    try {
      // field_config=all ensures Redirect_URL, Newsletter_ID and all fields are returned
      const response = await callZohoAPI(
        `${SUBMISSION_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Submission', err, { id })
    }
  },

  create: async (data: Partial<Omit<SubmissionRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(SUBMISSION_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Submission', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<SubmissionRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      // SAFETY: Never send Confirmed_At to Zoho — it rejects all date formats we send
      const sanitized = { ...data } as Record<string, any>
      delete sanitized.Confirmed_At
      delete sanitized.confirmed_at

      const response = await callZohoAPI(
        `${SUBMISSION_REPORT}/${id}`,
        'PATCH',
        { data: sanitized },
      )
      return response
    } catch (err) {
      logAndThrow('Update Submission', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${SUBMISSION_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Submission', err, { id })
    }
  },

  findByNewsletterId: async (
    newsletterId: string,
  ): Promise<SubmissionRecord[]> => {
    try {
      const result = await submissionsApi.list({ maxRecords: 200 })
      const all = result.data || result
      return all.filter(
        (s: SubmissionRecord) => s.Newsletter_ID === newsletterId,
      )
    } catch {
      return []
    }
  },

  findByToken: async (
    token: string,
  ): Promise<{ record: SubmissionRecord | null; diagnostics: string }> => {
    const diag: string[] = []

    // Strategy 1: Try filter search with field_config=all to ensure all fields returned
    try {
      const filters: CreatorFilter[] = [
        { field: 'Confirmation_Token', value: token },
      ]
      console.log(
        'findByToken [filter]: searching with Confirmation_Token filter',
      )
      const response = await callZohoAPI(
        `${SUBMISSION_REPORT}?field_config=all`,
        'GET',
        { filters },
      )
      const code = response?.code
      const dataCount = response?.data?.length ?? 0
      console.log('findByToken [filter]: code =', code, 'count =', dataCount)
      diag.push(`filter: code=${code}, count=${dataCount}`)
      const { data: records } = handleListResponse<SubmissionRecord>(response)
      if (records.length > 0) {
        console.log(
          'findByToken [filter]: found record ID =',
          records[0].ID,
          'token =',
          records[0].Confirmation_Token,
        )
        return { record: records[0], diagnostics: diag.join(' | ') }
      }
      // Log the raw response shape so we can see if Zoho returned something unexpected
      const responseShape = response
        ? `keys=[${Object.keys(response).join(',')}] dataType=${typeof response.data} isArray=${Array.isArray(response.data)}`
        : 'null'
      diag.push(`filter: 0 matches (${responseShape})`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.warn('findByToken [filter]: failed -', msg)
      diag.push(`filter: ERROR ${msg.substring(0, 200)}`)
    }

    // Strategy 2: Fallback — fetch recent submissions and filter client-side
    try {
      console.log(
        'findByToken [fallback]: fetching submissions with field_config=all...',
      )
      const url = `${SUBMISSION_REPORT}?max_records=2000&field_config=all`
      const response = await callZohoAPI(url)
      const { data: all } = handleListResponse<SubmissionRecord>(response)
      console.log('findByToken [fallback]: fetched', all.length, 'records')
      diag.push(`fallback: fetched ${all.length} records`)

      // Check how many have Confirmation_Token at all
      const withToken = all.filter((s) => s.Confirmation_Token)
      diag.push(`withToken: ${withToken.length}/${all.length}`)

      const match = all.find((s) => s.Confirmation_Token === token)
      if (match) {
        console.log('findByToken [fallback]: exact match found, ID =', match.ID)
        return { record: match, diagnostics: diag.join(' | ') }
      }

      // Try trimmed comparison in case of whitespace
      const trimMatch = all.find(
        (s) => s.Confirmation_Token?.trim() === token.trim(),
      )
      if (trimMatch) {
        console.log(
          'findByToken [fallback]: trimmed match found, ID =',
          trimMatch.ID,
        )
        return { record: trimMatch, diagnostics: diag.join(' | ') }
      }

      diag.push('fallback: no match')
      console.log('findByToken [fallback]: no match found for token:', token)
      return { record: null, diagnostics: diag.join(' | ') }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err)
      console.error('findByToken [fallback]: failed -', msg)
      diag.push(`fallback: ERROR ${msg.substring(0, 200)}`)
      return { record: null, diagnostics: diag.join(' | ') }
    }
  },

  confirm: async (
    token: string,
    recordId?: string,
  ): Promise<{
    success: boolean
    error?: string
    errorDetail?: string
    diagnostics?: string
    submission?: SubmissionRecord
  }> => {
    try {
      console.log('=== CONFIRM v9 === token:', token, 'recordId:', recordId)

      let submission: SubmissionRecord | null = null

      // Strategy A: If record ID is provided, fetch directly — most reliable
      if (recordId) {
        try {
          console.log('v8: Fetching by record ID:', recordId)
          const fetched = await submissionsApi.get(recordId)
          if (fetched) {
            // Verify token matches
            if (
              fetched.Confirmation_Token &&
              fetched.Confirmation_Token.trim() !== token.trim()
            ) {
              console.warn(
                'v8: Token mismatch! URL token:',
                token,
                'Record token:',
                fetched.Confirmation_Token,
              )
              return {
                success: false,
                error: 'invalid_token',
                errorDetail: `Token mismatch for record ${recordId}.`,
              }
            }
            submission = fetched
            console.log(
              'v8: Found by ID, Status:',
              submission.Status,
              'Redirect_URL:',
              submission.Redirect_URL,
            )
          }
        } catch (fetchErr) {
          console.warn('v8: Direct fetch by ID failed:', fetchErr)
        }
      }

      // Strategy B: Fall back to token search with retry
      // Mobile browsers (especially Gmail in-app browser on iPhone) have flakier
      // connections that can cause the first API call to fail. Retry up to 3 times
      // with increasing delays to handle transient network issues.
      const allDiagnostics: string[] = []
      if (!submission) {
        const MAX_TOKEN_RETRIES = 4
        const RETRY_DELAYS = [0, 3000, 6000, 10000] // immediate, 3s, 6s, 10s — total ~19s
        for (let attempt = 0; attempt < MAX_TOKEN_RETRIES; attempt++) {
          if (attempt > 0) {
            console.log(
              `v8: Retry ${attempt}/${MAX_TOKEN_RETRIES - 1}, waiting ${RETRY_DELAYS[attempt]}ms...`,
            )
            await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]))
          }
          console.log(`v8: findByToken attempt ${attempt + 1}...`)
          const result = await submissionsApi.findByToken(token)
          allDiagnostics.push(`[attempt${attempt + 1}] ${result.diagnostics}`)
          if (result.record) {
            submission = result.record
            console.log(
              `v8: Found on attempt ${attempt + 1}, ID:`,
              submission.ID,
            )
            break
          }
        }

        if (!submission) {
          const ua =
            typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
          const fullUrl =
            typeof window !== 'undefined' ? window.location.href : 'unknown'
          const diagStr = allDiagnostics.join(' ;; ')
          // Log to in-memory error log (visible if same browser tab)
          errorLogService.log(
            'Confirm — Token Not Found',
            `Token "${token}" not found after ${MAX_TOKEN_RETRIES} attempts`,
            {
              token,
              recordId: recordId || 'none',
              diagnostics: diagStr,
              userAgent: ua,
              url: fullUrl,
            },
          )
          console.error('v9: ALL DIAGNOSTICS:', diagStr)
          // Also try to persist the error to Zoho Creator by creating a submission
          // record with the error info — this way we can see it from any device
          try {
            await callZohoAPI(`${SUBMISSION_FORM}`, 'POST', {
              data: {
                Form_Payload: JSON.stringify({
                  error: 'token_not_found',
                  token,
                  diagnostics: diagStr,
                  ua: ua.substring(0, 200),
                  url: fullUrl,
                }),
                Status: 'Error',
                Confirmation_Token: `ERROR_${token}`,
                Team: 'system',
              },
            })
          } catch {
            /* best-effort */
          }
        }
      }

      if (!submission) {
        const ua =
          typeof navigator !== 'undefined' ? navigator.userAgent : 'unknown'
        const diagStr = allDiagnostics.join(' ;; ')
        return {
          success: false,
          error: 'invalid_token',
          errorDetail: `No submission found for token "${token}"${recordId ? ` (ID: ${recordId})` : ''}.`,
          diagnostics: `UA: ${ua.substring(0, 100)} | ${diagStr}`,
        }
      }

      console.log(
        'v8: Submission found, ID:',
        submission.ID,
        'Status:',
        submission.Status,
      )

      if (submission.Status === 'Confirmed') {
        console.log('v8: Already confirmed')
        return { success: true, submission }
      }

      // PATCH status to Confirmed — use submissionsApi.update() to go through
      // the same code path as all other submission updates (supports new database)
      try {
        console.log(
          'v10: Updating Status=Confirmed for ID:',
          submission.ID,
          'via submissionsApi.update()',
        )
        const result = await submissionsApi.update(submission.ID, {
          Status: 'Confirmed',
        } as any)
        console.log('v10: Update result:', JSON.stringify(result))

        // Verify the update actually took effect
        try {
          const verified = await submissionsApi.get(submission.ID)
          if (verified) {
            console.log(
              'v10: POST-UPDATE verification — Status:',
              verified.Status,
              '(expected: Confirmed)',
            )
            if (verified.Status !== 'Confirmed') {
              console.error(
                'v10: ❌ STATUS NOT UPDATED! Record still shows:',
                verified.Status,
              )
              errorLogService.log(
                'Confirm Submission — Status Not Updated',
                `Record ${submission.ID} still shows Status="${verified.Status}" after update`,
                {
                  updateResult: result,
                  verifiedStatus: verified.Status,
                },
              )
            } else {
              console.log('v10: ✅ Status verified as Confirmed')
            }
          }
        } catch (verifyErr) {
          console.warn(
            'v10: Could not verify update (non-blocking):',
            verifyErr,
          )
        }
      } catch (updateErr: any) {
        const msg =
          updateErr instanceof Error ? updateErr.message : String(updateErr)
        console.error('v10: Update failed:', msg)
        errorLogService.log('Confirm Submission', msg, {
          sentPayload: { Status: 'Confirmed' },
          debug: updateErr?.debug,
        })
        return {
          success: false,
          error: 'server_error',
          errorDetail: `Update failed for ${submission.ID}: ${msg}`,
        }
      }

      return {
        success: true,
        submission: { ...submission, Status: 'Confirmed' },
      }
    } catch (err: any) {
      const detail = err instanceof Error ? err.message : String(err)
      console.error('v8: ERROR', detail)
      return { success: false, error: 'server_error', errorDetail: detail }
    }
  },
}

// ── Teams CRUD ──

export const teamsApi = {
  list: async (): Promise<TeamRecord[]> => {
    try {
      const response = await callZohoAPI(TEAM_REPORT)
      return handleListResponse<TeamRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Teams', err)
    }
  },

  get: async (id: string): Promise<TeamRecord | null> => {
    try {
      const response = await callZohoAPI(`${TEAM_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Team', err, { id })
    }
  },

  findByTeamName: async (teamName: string): Promise<TeamRecord | null> => {
    try {
      const all = await teamsApi.list()
      return all.find((t) => t.Team_Name === teamName) || null
    } catch {
      return null
    }
  },

  findByTeamId: async (teamId: string): Promise<TeamRecord | null> => {
    try {
      const all = await teamsApi.list()
      return all.find((t) => t.Team_ID === teamId) || null
    } catch {
      return null
    }
  },

  create: async (data: Partial<Omit<TeamRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(TEAM_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Team', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<TeamRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${TEAM_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Team', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${TEAM_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Team', err, { id })
    }
  },

  generateTeamId: (teamName: string): string => {
    return teamName
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
  },
}

// ── Campaigns CRUD ──

export const campaignsApi = {
  list: async (): Promise<CampaignRecord[]> => {
    try {
      // field_config=all ensures ALL form fields are returned, not just quick_view fields
      // See: https://www.zoho.com/creator/help/api/v2.1/get-records.html
      const response = await callZohoAPI(`${CAMPAIGN_REPORT}?field_config=all`)
      return handleListResponse<CampaignRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Campaigns', err)
    }
  },

  get: async (id: string): Promise<CampaignRecord | null> => {
    try {
      // field_config=all ensures ALL form fields are returned
      const response = await callZohoAPI(
        `${CAMPAIGN_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Campaign', err, { id })
    }
  },

  create: async (data: Partial<Omit<CampaignRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(CAMPAIGN_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Campaign', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<CampaignRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${CAMPAIGN_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Campaign', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${CAMPAIGN_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Campaign', err, { id })
    }
  },

  generateCampaignId: (): string => {
    const timestamp = Date.now().toString(36)
    const random = Math.random().toString(36).substring(2, 8)
    return `cmp_${timestamp}_${random}`
  },

  generateBucketId: (teamId: string): string => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    const randomSuffix = String.fromCharCode(
      65 + Math.floor(Math.random() * 26),
    )
    return `${teamId.toUpperCase()}_${year}_${month}_${day}_${randomSuffix}`
  },
}

// ── Users CRUD ──

export const usersApi = {
  list: async (): Promise<UserRecord[]> => {
    try {
      const response = await callZohoAPI(USER_REPORT)
      return handleListResponse<UserRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Users', err)
    }
  },

  get: async (id: string): Promise<UserRecord | null> => {
    try {
      const response = await callZohoAPI(`${USER_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get User', err, { id })
    }
  },

  findByTeam: async (teamName: string): Promise<UserRecord[]> => {
    try {
      const all = await usersApi.list()
      return all.filter((u) => u.Team === teamName)
    } catch {
      return []
    }
  },

  create: async (data: Partial<Omit<UserRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(USER_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create User', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<UserRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${USER_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update User', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${USER_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete User', err, { id })
    }
  },
}

// ── Roles CRUD ──

export const rolesApi = {
  list: async (): Promise<RoleRecord[]> => {
    try {
      const response = await callZohoAPI(ROLE_REPORT)
      return handleListResponse<RoleRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Roles', err)
    }
  },

  get: async (id: string): Promise<RoleRecord | null> => {
    try {
      const response = await callZohoAPI(`${ROLE_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Role', err, { id })
    }
  },

  create: async (data: Partial<Omit<RoleRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(ROLE_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Role', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<RoleRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${ROLE_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Role', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${ROLE_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Role', err, { id })
    }
  },
}

// ── Audiences CRUD ──

export const audiencesCreatorApi = {
  list: async (): Promise<AudienceRecord[]> => {
    try {
      const response = await callZohoAPI(`${AUDIENCE_REPORT}?field_config=all`)
      return handleListResponse<AudienceRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Audiences', err)
    }
  },

  get: async (id: string): Promise<AudienceRecord | null> => {
    try {
      const response = await callZohoAPI(
        `${AUDIENCE_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Audience', err, { id })
    }
  },

  findByAudienceId: async (
    audienceId: string,
  ): Promise<AudienceRecord | null> => {
    try {
      const all = await audiencesCreatorApi.list()
      return all.find((a) => a.Audience_ID === audienceId) || null
    } catch {
      return null
    }
  },

  create: async (data: Partial<Omit<AudienceRecord, 'ID'>>): Promise<any> => {
    try {
      const response = await callZohoAPI(AUDIENCE_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Audience', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<AudienceRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(`${AUDIENCE_REPORT}/${id}`, 'PATCH', {
        data,
      })
      return response
    } catch (err) {
      logAndThrow('Update Audience', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${AUDIENCE_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Audience', err, { id })
    }
  },
}

// ── Form Templates Library CRUD ──

export const formTemplatesLibraryApi = {
  list: async (): Promise<FormTemplateRecord[]> => {
    try {
      const response = await callZohoAPI(
        `${FORM_TEMPLATE_REPORT}?field_config=all`,
      )
      return handleListResponse<FormTemplateRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Form Templates Library', err)
    }
  },

  get: async (id: string): Promise<FormTemplateRecord | null> => {
    try {
      const response = await callZohoAPI(
        `${FORM_TEMPLATE_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Form Template', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<FormTemplateRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(FORM_TEMPLATE_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Form Template', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<FormTemplateRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_TEMPLATE_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Form Template', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_TEMPLATE_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Form Template', err, { id })
    }
  },
}

// ── Email Template Library CRUD ──

export const emailTemplateLibraryApi = {
  list: async (): Promise<EmailLibraryRecord[]> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_LIBRARY_REPORT}?field_config=all`,
      )
      return handleListResponse<EmailLibraryRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Email Template Library', err)
    }
  },

  get: async (id: string): Promise<EmailLibraryRecord | null> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_LIBRARY_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Email Library Template', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<EmailLibraryRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(EMAIL_LIBRARY_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Email Library Template', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<EmailLibraryRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_LIBRARY_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Email Library Template', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_LIBRARY_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Email Library Template', err, { id })
    }
  },
}

// ── Landing Pages CRUD ──

export const landingPagesApi = {
  list: async (): Promise<LandingPageRecord[]> => {
    try {
      const response = await callZohoAPI(
        `${LANDING_PAGE_REPORT}?field_config=all`,
      )
      return handleListResponse<LandingPageRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Landing Pages', err)
    }
  },

  get: async (id: string): Promise<LandingPageRecord | null> => {
    try {
      const response = await callZohoAPI(
        `${LANDING_PAGE_REPORT}/${id}?field_config=all`,
      )
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Landing Page', err, { id })
    }
  },

  findBySlug: async (slug: string): Promise<LandingPageRecord | null> => {
    try {
      const all = await landingPagesApi.list()
      return all.find((p) => p.Url_Slug === slug) || null
    } catch (err) {
      // Only swallow "no records" errors — propagate real API failures
      if (isNoRecordsError(err)) return null
      throw err
    }
  },

  create: async (
    data: Partial<Omit<LandingPageRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(LANDING_PAGE_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Landing Page', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<LandingPageRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${LANDING_PAGE_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Landing Page', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${LANDING_PAGE_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Landing Page', err, { id })
    }
  },
}

// ── Media Library CRUD ──

export const mediaLibraryApi = {
  list: async (): Promise<MediaLibraryRecord[]> => {
    try {
      const response = await callZohoAPI(MEDIA_LIBRARY_REPORT)
      return handleListResponse<MediaLibraryRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Media Library', err)
    }
  },

  get: async (id: string): Promise<MediaLibraryRecord | null> => {
    try {
      const response = await callZohoAPI(`${MEDIA_LIBRARY_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Media Library', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<MediaLibraryRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(MEDIA_LIBRARY_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Media Library', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<MediaLibraryRecord, 'ID'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${MEDIA_LIBRARY_REPORT}/${id}`,
        'PATCH',
        {
          data,
        },
      )
      return response
    } catch (err) {
      logAndThrow('Update Media Library', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${MEDIA_LIBRARY_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Media Library', err, { id })
    }
  },
}

// ── Audit Log CRUD ──

export const auditLogApi = {
  list: async (options?: {
    filters?: CreatorFilter[]
    offset?: number
    only_count?: boolean
  }): Promise<CreatorPaginatedResult<AuditLogRecord>> => {
    try {
      const params: Record<string, any> = {}
      if (options?.offset) params.offset = options.offset
      if (options?.filters && options.filters.length > 0)
        params.filters = options.filters
      if (options?.only_count) params.only_count = true
      const response = await callZohoAPI(
        AUDIT_LOG_REPORT,
        'GET',
        Object.keys(params).length > 0 ? params : undefined,
      )
      if (options?.only_count) {
        return {
          data: [],
          total_count: parseInt(response?.total_count, 10) || 0,
        }
      }
      return handleListResponse<AuditLogRecord>(response)
    } catch (err) {
      if (isNoRecordsError(err)) return { data: [], total_count: 0 }
      logAndThrow('List Audit Logs', err)
    }
  },

  get: async (id: string): Promise<AuditLogRecord | null> => {
    try {
      const response = await callZohoAPI(`${AUDIT_LOG_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Audit Log', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<AuditLogRecord, 'ID' | 'created'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(AUDIT_LOG_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Audit Log', err, data)
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(`${AUDIT_LOG_REPORT}/${id}`, 'DELETE')
      return response
    } catch (err) {
      logAndThrow('Delete Audit Log', err, { id })
    }
  },
}

// ── Form Versions CRUD ──

export const formVersionsApi = {
  list: async (): Promise<FormVersionRecord[]> => {
    try {
      const response = await callZohoAPI(FORM_VERSION_REPORT)
      return handleListResponse<FormVersionRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Form Versions', err)
    }
  },

  listByFormId: async (formId: string): Promise<FormVersionRecord[]> => {
    try {
      const filters: CreatorFilter[] = [{ field: 'Form_ID', value: formId }]
      const response = await callZohoAPI(FORM_VERSION_REPORT, 'GET', {
        filters,
      })
      return handleListResponse<FormVersionRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Form Versions by Form ID', err, { formId })
    }
  },

  get: async (id: string): Promise<FormVersionRecord | null> => {
    try {
      const response = await callZohoAPI(`${FORM_VERSION_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Form Version', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<FormVersionRecord, 'ID' | 'Added_Time'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(FORM_VERSION_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Form Version', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<FormVersionRecord, 'ID' | 'Added_Time'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_VERSION_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Form Version', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${FORM_VERSION_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Form Version', err, { id })
    }
  },
}

// ── Email Template Versions CRUD ──

export const emailTemplateVersionsApi = {
  list: async (): Promise<EmailTemplateVersionRecord[]> => {
    try {
      const response = await callZohoAPI(EMAIL_VERSION_REPORT)
      return handleListResponse<EmailTemplateVersionRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Email Template Versions', err)
    }
  },

  listByTemplateId: async (
    templateId: string,
  ): Promise<EmailTemplateVersionRecord[]> => {
    try {
      const filters: CreatorFilter[] = [
        { field: 'Template_ID', value: templateId },
      ]
      const response = await callZohoAPI(EMAIL_VERSION_REPORT, 'GET', {
        filters,
      })
      return handleListResponse<EmailTemplateVersionRecord>(response).data
    } catch (err) {
      if (isNoRecordsError(err)) return []
      logAndThrow('List Email Template Versions by Template ID', err, {
        templateId,
      })
    }
  },

  get: async (id: string): Promise<EmailTemplateVersionRecord | null> => {
    try {
      const response = await callZohoAPI(`${EMAIL_VERSION_REPORT}/${id}`)
      return response?.data || null
    } catch (err) {
      logAndThrow('Get Email Template Version', err, { id })
    }
  },

  create: async (
    data: Partial<Omit<EmailTemplateVersionRecord, 'ID' | 'Added_Time'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(EMAIL_VERSION_FORM, 'POST', { data })
      return response
    } catch (err) {
      logAndThrow('Create Email Template Version', err, data)
    }
  },

  update: async (
    id: string,
    data: Partial<Omit<EmailTemplateVersionRecord, 'ID' | 'Added_Time'>>,
  ): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_VERSION_REPORT}/${id}`,
        'PATCH',
        { data },
      )
      return response
    } catch (err) {
      logAndThrow('Update Email Template Version', err, { id, ...data })
    }
  },

  delete: async (id: string): Promise<any> => {
    try {
      const response = await callZohoAPI(
        `${EMAIL_VERSION_REPORT}/${id}`,
        'DELETE',
      )
      return response
    } catch (err) {
      logAndThrow('Delete Email Template Version', err, { id })
    }
  },
}

// ── Form Submission Handler ──

export interface FormSubmissionData {
  newsletterId: string
  formFields: Record<string, string>
  team: string
  region?: string
  redirectUrl?: string
}

/** Generate a URL-safe confirmation token */
function generateConfirmationToken(): string {
  const timestamp = Date.now().toString(36)
  const randomPart = Array.from({ length: 16 }, () =>
    Math.floor(Math.random() * 36).toString(36),
  ).join('')
  return `${timestamp}_${randomPart}`
}

/**
 * Replace all {{variable}} placeholders in a string with actual values.
 * Works with any number of dynamic form fields.
 */
function renderTemplate(
  template: string,
  variables: Record<string, string>,
): string {
  let rendered = template
  for (const [key, value] of Object.entries(variables)) {
    // Replace both {{key}} and {{ key }} (with optional spaces)
    const regex = new RegExp(`\\{\\{\\s*${escapeRegex(key)}\\s*\\}\\}`, 'gi')
    rendered = rendered.replace(regex, value || '')
  }
  return rendered
}

function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Build the full confirmation URL for a specific submission token.
 * Uses the app's own /confirm route as the intermediary.
 */
function buildConfirmationUrl(token: string, recordId?: string): string {
  // Use the current app's origin for the confirmation endpoint
  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://your-app-domain.com'
  let url = `${origin}/confirm?token=${encodeURIComponent(token)}`
  if (recordId) {
    url += `&id=${encodeURIComponent(recordId)}`
  }
  return url
}

/**
 * Render an email template with all variables replaced.
 * Handles: form fields, confirmation_link, team_name, and any custom variables.
 */
async function renderEmailForSubmission(params: {
  templateId: string
  formFields: Record<string, string>
  confirmationToken: string
  team: string
  recordId?: string
}): Promise<{ renderedHtml: string; renderedSubject: string } | null> {
  try {
    const template = await emailTemplatesApi.get(params.templateId)
    if (!template) return null

    // Build the variable map from form fields + system variables
    const variables: Record<string, string> = {
      ...params.formFields,
      confirmation_link: buildConfirmationUrl(
        params.confirmationToken,
        params.recordId,
      ),
      team_name: params.team,
    }

    const renderedHtml = renderTemplate(
      template.Email_HTML_Body || '',
      variables,
    )
    const renderedSubject = renderTemplate(
      template.Email_Subject || '',
      variables,
    )

    return { renderedHtml, renderedSubject }
  } catch (err) {
    console.error('Failed to render email template:', err)
    return null
  }
}

/**
 * Look up the email template linked to a form via Form Configurations.
 * Evaluates conditional routing rules first, then falls back to the default template.
 */
async function findTemplateForForm(
  formId: string,
  formFields: Record<string, string>,
): Promise<string | null> {
  try {
    // 1. Check for conditional rules in the Signup Form
    const formRecord = await signupFormsApi.get(formId)
    if (formRecord?.Builder_Schema) {
      try {
        const schema = JSON.parse(formRecord.Builder_Schema)
        if (
          schema.emailRoutingRules &&
          Array.isArray(schema.emailRoutingRules)
        ) {
          for (const rule of schema.emailRoutingRules) {
            const fVal = (formFields[rule.field] || '').trim().toLowerCase()
            const rVal = (rule.value || '').trim().toLowerCase()
            let match = false
            if (rule.operator === 'equals' && fVal === rVal) match = true
            else if (rule.operator === 'not_equals' && fVal !== rVal)
              match = true
            else if (rule.operator === 'contains' && fVal.includes(rVal))
              match = true

            if (match && rule.templateId) {
              return rule.templateId
            }
          }
        }
      } catch (e) {
        // ignore parse error
      }
    }

    // 2. Fallback to default template
    const configs = await formConfigsApi.findByFormId(formId)
    if (configs.length === 0) return null
    return extractLookupId(configs[0].Email_Template) || null
  } catch {
    return null
  }
}

export async function handleFormSubmission(
  data: FormSubmissionData & { formId?: string },
): Promise<{ response: any; confirmationToken: string }> {
  const confirmationToken = generateConfirmationToken()

  const payload = {
    newsletter_id: data.newsletterId,
    ...data.formFields,
    submitted_at: new Date().toISOString(),
  }

  const submissionRecord: Record<string, any> = {
    Form_Payload: JSON.stringify(payload),
    Status: 'Not Subscribed',
    Newsletter_ID: data.newsletterId,
    Team: data.team,
    Region: data.region || '',
    Confirmation_Token: confirmationToken,
    Added_Time: new Date().toISOString(),
  }

  // Store the redirect URL so the /confirm page knows where to send the subscriber
  if (data.redirectUrl) {
    submissionRecord.Redirect_URL = data.redirectUrl
  }

  // Extract subscriber email from form fields (check common field names)
  const emailField =
    data.formFields.email ||
    data.formFields.Email ||
    data.formFields.email_address ||
    data.formFields.EMAIL ||
    ''
  if (emailField) {
    submissionRecord.Subscriber_Email = emailField
  }

  // Phase 1: Create the submission record WITHOUT rendered email first
  // so we get the record ID for the confirmation URL

  const response = await submissionsApi.create(submissionRecord)

  // Phase 2: Extract the record ID from the creation response and render
  // the email with the record ID included in the confirmation URL
  let recordId: string | undefined
  try {
    // Zoho Creator returns { code: 3000, data: { ID: "..." } }
    recordId = response?.data?.ID
    if (!recordId && response?.data) {
      // Sometimes the ID is nested differently
      recordId =
        typeof response.data === 'object' ? response.data.ID : undefined
    }
  } catch {
    // Ignore — we'll fall back to token-only URL
  }

  if (data.formId) {
    const templateId = await findTemplateForForm(data.formId, data.formFields)

    if (templateId) {
      const rendered = await renderEmailForSubmission({
        templateId,
        formFields: data.formFields,
        confirmationToken,
        team: data.team,
        recordId,
      })
      if (rendered && recordId) {
        // Phase 3: Update the submission with the rendered email that contains
        // the confirmation URL with the record ID for reliable lookup
        try {
          await submissionsApi.update(recordId, {
            Rendered_Email_HTML: rendered.renderedHtml,
            Rendered_Email_Subject: rendered.renderedSubject,
          } as any)
          console.log(
            '[submit] Updated submission',
            recordId,
            'with rendered email containing record ID in confirmation URL',
          )
        } catch (updateErr) {
          console.warn(
            '[submit] Failed to update rendered email on submission:',
            updateErr,
          )
          // Non-fatal — the submission was still created, just without rendered email
        }
      } else if (rendered) {
        // No record ID available — still try to update with token-only URL
        console.warn(
          '[submit] No record ID from create response, rendered email uses token-only URL',
        )
      }
    }
  }

  return { response, confirmationToken }
}

// ── Shared Helpers (exported) ──

export function extractLookupId(val: any): string {
  if (!val) return ''
  if (typeof val === 'object' && val.ID) return String(val.ID)
  if (typeof val === 'number') return String(val)
  if (typeof val === 'string') return val
  return ''
}

export function extractLookupDisplay(val: any): string {
  if (!val) return ''
  if (typeof val === 'object' && val.display_value) return val.display_value
  if (typeof val === 'object' && val.ID) return String(val.ID)
  if (typeof val === 'string') return val
  return ''
}
